/**
 * markdown-lite — a tiny, SAFE markdown renderer for agent replies and other
 * LLM-generated text in ArcLog OS.
 *
 * The problem it closes: agent replies are markdown (`###`, `**bold**`,
 * bullet lists), but every surface that showed them (Comms converse, the
 * Trading desk chat) rendered with `textContent` — so users read raw
 * `**asterisks**` and `####` hashes instead of formatted text.
 *
 * SAFETY CONTRACT (why this is one function, not innerHTML = text):
 *   1. The ENTIRE input is HTML-escaped first. Nothing in the source text —
 *      including `<script>` or a stray `</div>` — can inject markup.
 *   2. Only the markdown syntax below is then translated into tags.
 *   3. No URLs are rendered as links (no href = no phishing vector from a
 *      model output); URLs stay visible text.
 *
 * Supported syntax (deliberately small — agents write short status reports):
 *   - `# … ######` headings (rendered as scaled bold lines)
 *   - `**bold**`, `*italic*` / `_italic_`, `` `code` ``
 *   - `- item` / `* item` unordered lists; `1. item` ordered lists
 *   - `---` horizontal rule
 *   - blank line = paragraph break; single newline = <br>
 *
 * PURE: no DOM reads, no clock. Returns an HTML string the caller assigns to
 * `innerHTML` of a bubble-sized container.
 */

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Inline formatting on an ALREADY-ESCAPED line: code spans first (their
 * content must not be re-formatted), then bold, then italic.
 */
function inlineMd(line) {
  let out = line;
  // Inline code: `x` — protect contents from bold/italic passes.
  const codes = [];
  out = out.replace(/`([^`]+)`/g, (_, c) => {
    codes.push(c);
    return `\u0000${codes.length - 1}\u0000`;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^\w])_([^_\n]+)_/g, "$1<em>$2</em>");
  out = out.replace(/\u0000(\d+)\u0000/g, (_, i) => `<code>${codes[Number(i)]}</code>`);
  return out;
}

/**
 * Render markdown-lite to a safe HTML string. Empty/whitespace input → "".
 * @param {string} text raw (possibly markdown) text
 * @returns {string} HTML safe for innerHTML
 */
export function renderMarkdown(text) {
  if (typeof text !== "string" || !text.trim()) return "";
  const lines = text.split(/\r?\n/);
  const html = [];
  let listType = null; // "ul" | "ol" | null
  let para = [];

  const flushPara = () => {
    if (para.length) {
      html.push(`<p>${para.map(inlineMd).join("<br>")}</p>`);
      para = [];
    }
  };
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = escapeHtml(raw);
    const trimmed = line.trim();

    if (!trimmed) {
      flushPara();
      closeList();
      continue;
    }

    // Heading: # … ######
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushPara();
      closeList();
      const level = Math.min(h[1].length, 6);
      // Scale headings down inside chat bubbles — an H1 in a bubble is a wall.
      const cls = level <= 2 ? "md-h md-h1" : level === 3 ? "md-h md-h2" : "md-h md-h3";
      html.push(`<div class="${cls}">${inlineMd(h[2])}</div>`);
      continue;
    }

    // Horizontal rule
    if (/^(---+|\*\*\*+|___+)$/.test(trimmed)) {
      flushPara();
      closeList();
      html.push("<hr class=\"md-hr\">");
      continue;
    }

    // Unordered list item: - / * / +
    const ul = trimmed.match(/^[-*+]\s+(.*)$/);
    if (ul) {
      flushPara();
      if (listType !== "ul") {
        closeList();
        html.push("<ul class=\"md-ul\">");
        listType = "ul";
      }
      html.push(`<li>${inlineMd(ul[1])}</li>`);
      continue;
    }

    // Ordered list item: 1. / 2)
    const ol = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
    if (ol) {
      flushPara();
      if (listType !== "ol") {
        closeList();
        html.push("<ol class=\"md-ol\">");
        listType = "ol";
      }
      html.push(`<li>${inlineMd(ol[2])}</li>`);
      continue;
    }

    // Plain paragraph line
    closeList();
    para.push(line);
  }
  flushPara();
  closeList();
  return html.join("");
}
